import React from "react";
import { message } from "antd";
import { templateService, TemplateDto, VariableMap } from "api/template.service";
import TemplateEditor, { MessageTemplate } from "./text-editor";

interface TemplateEditorPageProps {
  organisationId: string;
  orgCode?: string;
  template: TemplateDto | null; // null = create mode
  variableMap: VariableMap;
  onBack: () => void;
  initialTriggers?: string[];
}

const extractVariables = (html: string): string[] => [
  ...new Set([...html.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1])),
];

const TemplateEditorPage: React.FC<TemplateEditorPageProps> = ({
  organisationId,
  orgCode,
  template,
  variableMap,
  onBack,
  initialTriggers,
}) => {
  const createTemplate = templateService.useCreateTemplate();
  const updateTemplate = templateService.useUpdateTemplate();

  const isCreating = template === null;
  const saving = createTemplate.isPending || updateTemplate.isPending;

  const handleSave = async (next: MessageTemplate) => {
    if (isCreating) {
      createTemplate.mutate(
        {
          name: next.name,
          subject: next.subject,
          body: next.body,
          triggers: next.triggers,
          recipients: next.recipients,
          organisation: organisationId,
        },
        {
          onSuccess: () => {
            message.success("Template created");
            onBack();
          },
          onError: () => {
            message.error("Failed to create template");
          },
        },
      );
    } else {
      updateTemplate.mutate(
        {
          id: template._id,
          body: {
            name: next.name,
            subject: next.subject,
            body: next.body,
            triggers: next.triggers,
            recipients: next.recipients,
          },
        },
        {
          onSuccess: () => {
            message.success("Template saved");
            onBack();
          },
          onError: () => {
            message.error("Failed to save template");
          },
        },
      );
    }
  };

  return (
    <TemplateEditor
      value={
        template
          ? {
              name: template.name,
              subject: template.subject,
              body: template.body,
              triggers: template.triggers,
              recipients: template.recipients,
            }
          : initialTriggers?.length
            ? { name: "", subject: "", body: "", triggers: initialTriggers, recipients: { to: [], cc: [] } }
            : undefined
      }
      variableMap={variableMap}
      onSave={handleSave}
      saving={saving}
      onBack={onBack}
      orgCode={orgCode}
    />
  );
};

export default TemplateEditorPage;
